import React, { useState, useEffect } from 'react';
import axios from 'axios';
import jwt_decode from 'jwt-decode';
import baseUrl from '../BaseUrl';
import Loader from '../Loader';
import Pagination from 'react-js-pagination';

// Sentinel for the "leave Call Pass Active as it is" option — '' already means "Any"
// (no Call Pass criteria on the record).
const KEEP_CURRENT = '__keep__';

const TNManagement = ({ token, expire, setToken, setExpire, setName, selectedCid }) => {
    const [viewMode, setViewMode] = useState('tn'); // 'tn' | 'details' | 'language'
    const [popupOpen, setPopupOpen] = useState(true);
    const [msg, setMsg] = useState('');
    const [isSuccess, setIsSuccess] = useState(false);

    // Shared dropdowns
    const [allDetails,  setAllDetails]  = useState([]);
    const [allLanguages, setAllLanguages] = useState([]);
    const [allRiskTypes, setAllRiskTypes] = useState([]);

    // By TN view
    const [tnSearch,    setTnSearch]    = useState('');
    const [tnRecord,    setTnRecord]    = useState(null);
    const [matchingTNs, setMatchingTNs] = useState([]);
    const [updateForm,  setUpdateForm]  = useState(null);
    const [scopeModal,  setScopeModal]  = useState(false);

    // By List view (for details/language sub-views we reuse RegistrationDetails/RegistrationLanguage components)
    // Here we handle the flat TN list + add/edit/delete
    const [tnList,      setTnList]      = useState([]);
    const [tnTotal,     setTnTotal]     = useState(0);
    const [tnPage,      setTnPage]      = useState(1);
    const [addForm,     setAddForm]     = useState(null);
    const [addErrors,   setAddErrors]   = useState({});

    useEffect(() => { if (token && selectedCid) { loadDropdowns(); loadTNList(); } }, [token, selectedCid]);
    useEffect(() => { if (token && selectedCid) loadTNList(); }, [tnPage]);

    const axiosJWT = axios.create();
    axiosJWT.interceptors.request.use(async (config) => {
        if (expire * 1000 < Date.now()) {
            const res = await axios.get(`${baseUrl}/token`);
            config.headers.Authorization = `Bearer ${res.data.accessToken}`;
            setToken(res.data.accessToken);
            const decoded = jwt_decode(res.data.accessToken);
            setName(decoded.name); setExpire(decoded.exp);
        }
        return config;
    }, err => Promise.reject(err));

    const showMsg = (text, success = true) => { setMsg(text); setIsSuccess(success); };

    const loadDropdowns = async () => {
        try {
            const [dRes, lRes, rRes] = await Promise.all([
                axiosJWT.get(`${baseUrl}/registration/setup/details/all?cid=${selectedCid}`, { headers: { Authorization: `Bearer ${token}` } }),
                axiosJWT.get(`${baseUrl}/registration/setup/language/all?cid=${selectedCid}`, { headers: { Authorization: `Bearer ${token}` } }),
                axiosJWT.get(`${baseUrl}/registration/admin/risk-types/all`, { headers: { Authorization: `Bearer ${token}` } })
            ]);
            setAllDetails(dRes.data.data);
            setAllLanguages(lRes.data.data);
            setAllRiskTypes(rRes.data.data);
        } catch (e) { console.log(e); }
    };

    const loadTNList = async () => {
        setPopupOpen(true);
        try {
            const res = await axiosJWT.get(`${baseUrl}/registration/setup/tn-list?cid=${selectedCid}&page=${tnPage}&limit=20`, { headers: { Authorization: `Bearer ${token}` } });
            setTnList(res.data.data); setTnTotal(res.data.totalItem);
        } catch (e) { console.log(e); }
        setPopupOpen(false);
    };

    const searchTN = async () => {
        if (!tnSearch.trim()) return;
        setPopupOpen(true);
        try {
            const res = await axiosJWT.get(`${baseUrl}/registration/setup/tn?tn=${tnSearch.trim()}&cid=${selectedCid}`, { headers: { Authorization: `Bearer ${token}` } });
            setTnRecord(res.data.record);
            setMatchingTNs(res.data.matchingTNs);
            if (res.data.record) {
                setUpdateForm({
                    reg_details_id: res.data.record.reg_details_id,
                    reg_lang_id: res.data.record.reg_lang_id,
                    call_pass_active: KEEP_CURRENT
                });
            }
        } catch (e) { showMsg('Error searching TN.', false); }
        setPopupOpen(false);
    };

    const handleTNUpdate = async (scope) => {
        try {
            setPopupOpen(true);
            const payload = { tn: tnSearch.trim(), cid: selectedCid, ...updateForm, scope };
            // Omitting call_pass_active tells the API to leave it untouched — sending ''
            // would clear it, which is a different thing.
            if (payload.call_pass_active === KEEP_CURRENT) delete payload.call_pass_active;
            const res = await axiosJWT.post(`${baseUrl}/registration/setup/tn/update`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            showMsg(res.data.msg); setScopeModal(false); searchTN();
        } catch (e) { showMsg(e.response?.data?.msg || 'Error updating.', false); }
        setPopupOpen(false);
    };

    const handleAddTN = async (e) => {
        e.preventDefault();
        const errs = {};
        if (!addForm.reg_details_id) errs.details = 'Registration Details required.';
        if (!addForm.reg_lang_id)    errs.lang    = 'Registration Language required.';
        if (addForm.tn && addForm.tag) errs.both = 'Cannot have both TN and Tag values.';
        if (addForm.registration_risk_type_id && addForm.has_risk != null && addForm.has_risk !== '') {
            errs.risk = 'registration_risk_type_id and has_risk are mutually exclusive.';
        }
        setAddErrors(errs);
        if (Object.keys(errs).length > 0) return;

        try {
            setPopupOpen(true);
            const res = await axiosJWT.post(`${baseUrl}/registration/setup/tn/save`,
                { ...addForm, bap_bbd_id: selectedCid },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            showMsg(res.data.msg); setAddForm(null); loadTNList();
        } catch (e) { showMsg(e.response?.data?.msg || 'Error saving.', false); }
        setPopupOpen(false);
    };

    const handleDeleteTN = async (id) => {
        if (!window.confirm('Delete this TN registration record?')) return;
        try {
            setPopupOpen(true);
            const res = await axiosJWT.delete(`${baseUrl}/registration/setup/tn/${id}`, { headers: { Authorization: `Bearer ${token}` } });
            showMsg(res.data.msg); loadTNList();
        } catch (e) { showMsg(e.response?.data?.msg || 'Error deleting.', false); }
        setPopupOpen(false);
    };

    const emptyAddForm = { tn: '', tag: '', reg_details_id: '', reg_lang_id: '', registration_risk_type_id: '', has_risk: '', call_pass_active: '' };

    // Call Pass Active is derived from the TN being Active in vw_Get_TNs_For_BAP_ID.
    // It is a separate criteria from TN / Tag — blank means it is not part of the match.
    const callPassLabel = (val) =>
        (val === 1 || val === '1') ? 'Active'
        : (val === 0 || val === '0') ? 'Not Active'
        : 'Any';

    return (
        <div>
            {popupOpen && <Loader />}
            <p className={`has-text-centered ${isSuccess ? 'success' : 'error'}`}>{msg}</p>

            {/* View Mode Selector */}
            <div style={{ marginBottom:'15px' }}>
                <strong>View By: </strong>
                {['tn', 'list'].map(mode => (
                    <label key={mode} style={{ marginRight:'15px', cursor:'pointer' }}>
                        <input type="radio" name="viewMode" checked={viewMode === mode} onChange={() => setViewMode(mode)} style={{ marginRight:'5px' }} />
                        {{ tn: 'By TN (search)', list: 'Full TN List' }[mode]}
                    </label>
                ))}
            </div>

            {/* ── By TN View ── */}
            {viewMode === 'tn' && (
                <div className="col-12 common-white-shadow-background setting-section">
                    <h4>Search by Telephone Number</h4>
                    <div style={{ display:'flex', gap:'10px', marginBottom:'15px' }}>
                        <input
                            type="text"
                            className="form-control"
                            style={{ maxWidth:'250px' }}
                            placeholder="Enter TN..."
                            value={tnSearch}
                            onChange={e => setTnSearch(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && searchTN()}
                        />
                        <button className="btn btn-primary" onClick={searchTN}>Search</button>
                    </div>

                    {tnRecord && (
                        <div>
                            <h5>TN: {tnSearch}</h5>
                            <div className="row" style={{ marginBottom:'15px' }}>
                                <div className="col-md-4">
                                    <strong>Registration Details:</strong> {tnRecord.registration_short_description || '—'}
                                </div>
                                <div className="col-md-4">
                                    <strong>Registration Language:</strong> {tnRecord.lang_short_description || '—'}
                                </div>
                                <div className="col-md-4">
                                    <strong>Call Pass Active:</strong> {callPassLabel(tnRecord.call_pass_active)}
                                </div>
                            </div>

                            {matchingTNs.length > 0 && (
                                <div style={{ marginBottom:'15px' }}>
                                    <strong>Other TNs with same Details + Language ({matchingTNs.length}):</strong>
                                    <div style={{ maxHeight:'150px', overflowY:'auto', border:'1px solid #ddd', padding:'8px', marginTop:'5px' }}>
                                        {matchingTNs.map((t, i) => <span key={i} style={{ marginRight:'10px' }}>{t.tn || t.tag}</span>)}
                                    </div>
                                </div>
                            )}

                            <div className="row">
                                <div className="col-md-3">
                                    <label>Change Details</label>
                                    <select className="form-control" value={updateForm?.reg_details_id || ''} onChange={e => setUpdateForm({...updateForm, reg_details_id: e.target.value})}>
                                        <option value="">— Keep current —</option>
                                        {allDetails.map(d => <option key={d.reg_details_id} value={d.reg_details_id}>{d.registration_short_description}</option>)}
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label>Change Language</label>
                                    <select className="form-control" value={updateForm?.reg_lang_id || ''} onChange={e => setUpdateForm({...updateForm, reg_lang_id: e.target.value})}>
                                        <option value="">— Keep current —</option>
                                        {allLanguages.map(l => <option key={l.reg_lang_id} value={l.reg_lang_id}>{l.short_description}</option>)}
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label>Change Call Pass Active</label>
                                    <select className="form-control" value={updateForm?.call_pass_active ?? KEEP_CURRENT} onChange={e => setUpdateForm({...updateForm, call_pass_active: e.target.value})}>
                                        <option value={KEEP_CURRENT}>— Keep current —</option>
                                        <option value="">Any</option>
                                        <option value="1">Active</option>
                                        <option value="0">Not Active</option>
                                    </select>
                                </div>
                                <div className="col-md-3" style={{ display:'flex', alignItems:'flex-end' }}>
                                    <button className="btn btn-primary" onClick={() => setScopeModal(true)}>Update</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {tnRecord === null && tnSearch && (
                        <p><em>No registration record found for {tnSearch}.</em></p>
                    )}

                    {/* Scope Modal */}
                    {scopeModal && (
                        <div className="modal-overlay" style={{ position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center' }}>
                            <div style={{ background:'#fff',padding:'30px',borderRadius:'8px',maxWidth:'450px',width:'90%' }}>
                                <h4>Update Scope</h4>
                                <p>Select <strong>ALL</strong> to update all telephone numbers listed as having the same criteria, or <strong>ONLY TN</strong> to update only telephone number <strong>{tnSearch}</strong>.</p>
                                <div style={{ display:'flex',gap:'10px',marginTop:'15px' }}>
                                    <button className="btn btn-primary" onClick={() => handleTNUpdate('all')}>ALL</button>
                                    <button className="btn btn-warning" onClick={() => handleTNUpdate('single')}>ONLY TN {tnSearch}</button>
                                    <button className="btn btn-secondary" onClick={() => setScopeModal(false)}>Cancel</button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ── Full TN List View ── */}
            {viewMode === 'list' && (
                <div className="col-12 common-white-shadow-background setting-section">
                    <div className="row">
                        <div className="col-6"></div>
                        <div className="col-6 inventory-btn">
                            {!addForm && (
                                <div className="addnew-button">
                                    <a href="#" onClick={e => { e.preventDefault(); setAddForm({ ...emptyAddForm }); setAddErrors({}); }}>Add</a>
                                </div>
                            )}
                        </div>
                    </div>

                    {addForm && (
                        <div style={{ padding:'15px',background:'#f9f9f9',borderRadius:'6px',marginBottom:'15px' }}>
                            <h4>Add TN Registration Record</h4>
                            <p style={{ color:'#666',fontSize:'0.9em' }}>A record can have TN or Tag (or neither), but not both. Call Pass Active is a separate criteria and can be combined with TN, Tag or neither.</p>
                            <div className="row">
                                <div className="col-md-3">
                                    <label>TN (telephone number)</label>
                                    <input type="text" className="form-control" value={addForm.tn} onChange={e => setAddForm({...addForm, tn: e.target.value})} />
                                </div>
                                <div className="col-md-3">
                                    <label>Tag</label>
                                    <input type="text" className="form-control" value={addForm.tag} onChange={e => setAddForm({...addForm, tag: e.target.value})} />
                                </div>
                                <div className="col-md-3">
                                    <label>Call Pass Active</label>
                                    <select className="form-control" value={addForm.call_pass_active} onChange={e => setAddForm({...addForm, call_pass_active: e.target.value})}>
                                        <option value="">— Any —</option>
                                        <option value="1">Active</option>
                                        <option value="0">Not Active</option>
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label>Registration Details *</label>
                                    <select className="form-control" value={addForm.reg_details_id} onChange={e => setAddForm({...addForm, reg_details_id: e.target.value})}>
                                        <option value="">— Select —</option>
                                        {allDetails.map(d => <option key={d.reg_details_id} value={d.reg_details_id}>{d.registration_short_description}</option>)}
                                    </select>
                                    {addErrors.details && <div className="error error-message">{addErrors.details}</div>}
                                </div>
                                <div className="col-md-3">
                                    <label>Registration Language *</label>
                                    <select className="form-control" value={addForm.reg_lang_id} onChange={e => setAddForm({...addForm, reg_lang_id: e.target.value})}>
                                        <option value="">— Select —</option>
                                        {allLanguages.map(l => <option key={l.reg_lang_id} value={l.reg_lang_id}>{l.short_description}</option>)}
                                    </select>
                                    {addErrors.lang && <div className="error error-message">{addErrors.lang}</div>}
                                </div>
                            </div>
                            <div className="row" style={{ marginTop:'10px' }}>
                                <div className="col-md-3">
                                    <label>Has Risk (0=No Risk, 1=Risk, blank=default)</label>
                                    <select className="form-control" value={addForm.has_risk} onChange={e => setAddForm({...addForm, has_risk: e.target.value, registration_risk_type_id: ''})}>
                                        <option value="">— None —</option>
                                        <option value="0">0 — No Risk (not spam)</option>
                                        <option value="1">1 — Has Risk (spam)</option>
                                    </select>
                                </div>
                                <div className="col-md-4">
                                    <label>Risk Type (leave blank if Has Risk is set)</label>
                                    <select className="form-control" value={addForm.registration_risk_type_id} onChange={e => setAddForm({...addForm, registration_risk_type_id: e.target.value, has_risk: ''})}>
                                        <option value="">— None —</option>
                                        {allRiskTypes.map(r => (
                                            <option key={r.registration_risk_type_id} value={r.registration_risk_type_id}>
                                                {r.risk_data_source} — {r.registration_risk_value}{r.registration_website_description ? ` (${r.registration_website_description})` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            {addErrors.both && <div className="error error-message">{addErrors.both}</div>}
                            {addErrors.risk && <div className="error error-message">{addErrors.risk}</div>}
                            <div style={{ marginTop:'10px', display:'flex', gap:'10px' }}>
                                <button className="btn btn-primary" onClick={handleAddTN}>Save</button>
                                <button className="btn btn-secondary" onClick={() => setAddForm(null)}>Cancel</button>
                            </div>
                        </div>
                    )}

                    <div className="fullwidth-table track-table-body">
                        <table>
                            <thead>
                                <tr><th style={{ width:'auto' }}>ID</th><th style={{ width:'auto' }}>TN</th><th style={{ width:'auto' }}>Tag</th><th style={{ width:'auto' }}>Call Pass Active</th><th style={{ width:'auto' }}>Details</th><th style={{ width:'auto' }}>Language</th><th style={{ width:'auto' }}>Has Risk</th><th style={{ width:'auto' }}>Delete</th></tr>
                            </thead>
                            <tbody>
                                {tnList.map((row, i) => (
                                    <tr key={i}>
                                        <td style={{ textAlign:'left' }}>{row.reg_tn_id}</td>
                                        <td style={{ textAlign:'left' }}>{row.tn || '—'}</td>
                                        <td style={{ textAlign:'left' }}>{row.tag || '—'}</td>
                                        <td style={{ textAlign:'left' }}>{callPassLabel(row.call_pass_active)}</td>
                                        <td style={{ textAlign:'left' }}>{row.registration_short_description || row.reg_details_id}</td>
                                        <td style={{ textAlign:'left' }}>{row.lang_short_description || row.reg_lang_id}</td>
                                        <td style={{ textAlign:'left' }}>
                                            {row.registration_risk_type_id
                                                // ENRMS-342: comma-separated list of risk type ids
                                                ? String(row.registration_risk_type_id).split(',').map(id => { const rt = allRiskTypes.find(r => String(r.registration_risk_type_id) === id.trim()); return rt ? `${rt.risk_data_source} — ${rt.registration_risk_value}` : `Risk Type: ${id.trim()}`; }).join(', ')
                                                : (row.has_risk === 0 || row.has_risk === '0') ? 'No Risk'
                                                : (row.has_risk === 1 || row.has_risk === '1') ? 'Has Risk'
                                                : 'Default'}
                                        </td>
                                        <td style={{ textAlign:'left' }}>
                                            <a href="#" onClick={e => { e.preventDefault(); handleDeleteTN(row.reg_tn_id); }}>
                                                <img src={`${process.env.PUBLIC_URL}/images/delete.png`} alt="Delete" />
                                            </a>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {tnTotal > 20 && <Pagination activePage={tnPage} itemsCountPerPage={20} totalItemsCount={tnTotal} pageRangeDisplayed={5} onChange={setTnPage} />}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TNManagement;
